import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, Video, CheckCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VideoCard from './VideoCard';
import NotificationBell from './NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoDashboard = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusCounts, setStatusCounts] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    fetchVideos();

    // Auto-refresh every 15 seconds when there are processing videos
    const interval = setInterval(() => {
      const hasProcessing = videos.some(v => 
        ['uploading', 'merging', 'compressing'].includes(v.status)
      );
      
      if (hasProcessing) {
        fetchVideos(true); // Silent refresh
      }
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const fetchVideos = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      const token = localStorage.getItem('token');
      const statusFilter = activeTab === 'all' ? '' : `?status=${activeTab}`;
      
      const response = await axios.get(
        `${API}/teacher/videos-with-status${statusFilter}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setVideos(response.data.videos);
      setStatusCounts(response.data.status_counts);
    } catch (error) {
      console.error('Error fetching videos:', error);
      if (!silent) {
        toast.error('فشل تحميل الفيديوهات');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchVideos();
    setRefreshing(false);
    toast.success('تم تحديث القائمة');
  };

  const handleValidateAll = async () => {
    setValidating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/teacher/videos/validate-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { issues_found, fixed_count, total_videos } = response.data;
      
      if (issues_found > 0) {
        toast.warning(`تم فحص ${total_videos} فيديو. وُجدت ${issues_found} مشكلة، تم إصلاح ${fixed_count} منها.`);
      } else {
        toast.success(`تم فحص ${total_videos} فيديو. جميع الفيديوهات سليمة!`);
      }
      
      // Refresh to show updated statuses
      await fetchVideos();
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('فشل فحص الفيديوهات');
    } finally {
      setValidating(false);
    }
  };

  const handleVideoDeleted = () => {
    fetchVideos(); // Refresh the list after deletion
  };

  const handleTabChange = async (value) => {
    setActiveTab(value);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const statusFilter = value === 'all' ? '' : `?status=${value}`;
      
      const response = await axios.get(
        `${API}/teacher/videos-with-status${statusFilter}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setVideos(response.data.videos);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('فشل تحميل الفيديوهات');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      all: 'الكل',
      uploading: 'جاري الرفع',
      merging: 'جاري الدمج',
      compressing: 'جاري الضغط',
      ready: 'جاهز',
      failed: 'فشل',
      file_missing: 'ملف مفقود'
    };
    return labels[status] || status;
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Video className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">إدارة الفيديوهات</h1>
            <p className="text-gray-600 mt-1">
              تتبع حالة تسجيلات الامتحانات
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <NotificationBell />
          <Button
            onClick={handleValidateAll}
            variant="outline"
            disabled={validating}
            className="gap-2"
          >
            <CheckCircle className={`w-4 h-4 ${validating ? 'animate-spin' : ''}`} />
            فحص الملفات
          </Button>
          <Button
            onClick={handleRefresh}
            variant="outline"
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        {[
          { key: 'all', label: 'الإجمالي', color: 'bg-blue-500' },
          { key: 'ready', label: 'جاهز', color: 'bg-green-500' },
          { key: 'compressing', label: 'قيد المعالجة', color: 'bg-yellow-500' },
          { key: 'uploading', label: 'جاري الرفع', color: 'bg-purple-500' },
          { key: 'failed', label: 'فشل', color: 'bg-red-500' },
          { key: 'file_missing', label: 'ملف مفقود', color: 'bg-orange-500' }
        ].map(({ key, label, color }) => {
          const count = key === 'all' 
            ? Object.values(statusCounts).reduce((sum, val) => sum + val, 0)
            : (statusCounts[key] || 0);

          return (
            <Card key={key}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{label}</p>
                    <p className="text-2xl font-bold mt-1">{count}</p>
                  </div>
                  <div className={`w-12 h-12 ${color} rounded-full opacity-20`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid grid-cols-7 w-full max-w-3xl mb-6">
          <TabsTrigger value="all">
            {getStatusLabel('all')} ({Object.values(statusCounts).reduce((sum, val) => sum + val, 0)})
          </TabsTrigger>
          <TabsTrigger value="ready">
            {getStatusLabel('ready')} ({statusCounts.ready || 0})
          </TabsTrigger>
          <TabsTrigger value="compressing">
            {getStatusLabel('compressing')} ({statusCounts.compressing || 0})
          </TabsTrigger>
          <TabsTrigger value="merging">
            {getStatusLabel('merging')} ({statusCounts.merging || 0})
          </TabsTrigger>
          <TabsTrigger value="uploading">
            {getStatusLabel('uploading')} ({statusCounts.uploading || 0})
          </TabsTrigger>
          <TabsTrigger value="failed">
            {getStatusLabel('failed')} ({statusCounts.failed || 0})
          </TabsTrigger>
          <TabsTrigger value="file_missing">
            {getStatusLabel('file_missing')} ({statusCounts.file_missing || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">جاري التحميل...</p>
            </div>
          ) : videos.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Video className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  لا توجد فيديوهات
                </h3>
                <p className="text-gray-500">
                  {activeTab === 'all' 
                    ? 'لم يتم رفع أي فيديوهات بعد'
                    : `لا توجد فيديوهات في حالة "${getStatusLabel(activeTab)}"`
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((video) => (
                <VideoCard
                  key={`${video.recording_id}-${video.video_type}`}
                  video={video}
                  onDownload={() => fetchVideos(true)}
                  onRetry={() => {
                    toast.info('سيتم تحديث الحالة خلال ثوانٍ...');
                    setTimeout(() => fetchVideos(true), 3000);
                  }}
                  onDelete={handleVideoDeleted}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VideoDashboard;
